import { CompanyRoleEnum } from "@boundless/bl-api-client/customer/models/CompanyRoleEnum"
import { customerCompanyList } from "@boundless/bl-api-client/customer/services"

import { store } from "@/lib/store"

import type { Company } from "@boundless/bl-api-client/customer/models/Company"

/**
 * Whether the requester is an Owner of this company rather than a Collaborator.
 *
 * Owner is the tier that manages people: inviting, cancelling an invitation,
 * changing a Company Role and removing a member, plus seeing who the members
 * and pending invitees are. Everything else reads the same for both tiers.
 *
 * Takes `undefined` so callers can pass a `find` straight in: a company that
 * is not in the account's list is not one the account owns.
 */
export function isCurrentUserOwner(company: Company | undefined): boolean {
  return company?.currentUserRole === CompanyRoleEnum.owner
}

/**
 * Options every observer of the customer company list must pass.
 *
 * The `queryFn` is overridden for two reasons: it fetches with the recency
 * ordering the default active company is picked from, and it resolves
 * `activeCompanyUuid` before the data is handed back, so router
 * `beforeLoad`/`loader` functions can rely on the store on a cold page load.
 *
 * Sharing it is not optional. A query holds a single `queryFn`, taken from the
 * last observer to render, and `invalidateQueries` refetches through the query
 * rather than through an observer. An observer left on the generated `queryFn`
 * therefore silently drops both the ordering and the resolution on the next
 * invalidation - which is how first-time onboarding stalls on the Business
 * Details step, with `activeCompanyUuid` never set for the company just created.
 *
 * @param enabled - Gate for callers that mount before the role is known
 */
export function companyListQueryOptions(enabled = true) {
  return {
    query: {
      enabled,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        customerCompanyList({ ordering: "last_modified_at" }, { signal }).then(
          (companies) => {
            store.setState({
              activeCompanyUuid: resolveActiveCompanyUuid(
                store.getState().activeCompanyUuid,
                companies.map((c) => c.uuid),
              ),
            })
            return companies
          },
        ),
    },
  }
}

/**
 * Resolves correct activeCompanyUuid based on current state and company list.
 *
 * | activeCompanyUuid | Has Companies  | Current Uuid in List | Action           |
 * |-------------------|----------------|----------------------|------------------|
 * | N                 | N              | N                    | Set null         |
 * | N                 | Y              | N                    | Pick first UUID  |
 * | Y                 | N              | N                    | Set null         |
 * | Y                 | Y              | N                    | Pick first UUID  |
 * | Y                 | Y              | Y                    | Do nothing       |
 *
 * @param currentUuid - The current activeCompanyUuid
 * @param companyUuids - The list of fetched company UUIDs
 * @returns The resolved activeCompanyUuid to be set in the store
 */
export function resolveActiveCompanyUuid(
  currentUuid: string | null,
  companyUuids: string[],
): string | null {
  if (currentUuid && companyUuids.includes(currentUuid)) {
    return currentUuid
  }

  return companyUuids.length ? companyUuids[0] : null
}
